import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';
import DocumentCanvas, { PlacedField } from '@/components/document-editor/document-canvas';
import DraggableField from '@/components/document-editor/draggable-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, ChevronLeft, SaveIcon, SendIcon, Plus } from 'lucide-react';
import { useDocumentCategories } from '@/hooks/useDocumentCategories';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import SendTemplateModal from '@/components/template-editor/send-template-modal';
import RecipientSelector, { RECIPIENT_COLORS, RECIPIENT_HEX_COLORS } from '@/components/template-editor/recipient-selector';
import AddCustomFieldModal from '@/components/template-editor/add-custom-field-modal';

const FIELD_TYPE_KEYS: Record<string, string> = {
  signature: 'editor_field_signature',
  initial: 'editor_field_initial',
  date: 'editor_field_date',
  name: 'editor_field_name',
  email: 'editor_field_email',
  city: 'editor_field_city',
  position: 'editor_field_position',
  province: 'editor_field_province',
  state: 'editor_field_state',
};

interface TemplateData {
  id?: string;
  template_title: string;
  document_category_id: string;
  notes?: string;
  file_path?: string;
  status?: string;
  fields: PlacedField[];
  file?: File;
  category?: { id: string; name: string };
}

interface ApiResponse<T> {
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
}

const TemplateEditor: React.FC = () => {
  const { t } = useTranslation(['templates', 'common']);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { categories } = useDocumentCategories();

  const fieldTypeLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [key, translationKey] of Object.entries(FIELD_TYPE_KEYS)) {
      labels[key] = t(`templates:${translationKey}`);
    }
    return labels;
  }, [t]);

  const [template, setTemplate] = useState<TemplateData>({
    template_title: '',
    document_category_id: '',
    notes: '',
    fields: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string>('');
  const [templateId, setTemplateId] = useState<string | undefined>(id);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  // Recipient state
  const [recipients, setRecipients] = useState<number[]>([1]);
  const [selectedRecipient, setSelectedRecipient] = useState<number>(1);

  // Custom fields state
  const [customFields, setCustomFields] = useState<{ id: number; field_name: string }[]>([]);
  const [addCustomFieldOpen, setAddCustomFieldOpen] = useState(false);

  // Header state
  const [category, setCategory] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const showToast = useCallback(({ title, description, variant = 'default' }: {
    title: string;
    description: string;
    variant?: 'default' | 'destructive' | 'success'
  }) => {
    toast({ title, description, variant });
  }, [toast]);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await api.get<any>(`/templates/${id}`) as any;
      const tmplData = response?.template || response?.data?.template || response?.data;

      if (tmplData) {
        // Load custom fields
        const loadedCustomFields = (tmplData.custom_fields || []).map((cf: any) => ({
          id: cf.id,
          field_name: cf.field_name,
        }));
        setCustomFields(loadedCustomFields);

        // Build a lookup for custom field names by ID
        const cfLookup: Record<number, string> = {};
        loadedCustomFields.forEach((cf: { id: number; field_name: string }) => { cfLookup[cf.id] = cf.field_name; });

        const formattedFields = (tmplData.fields || []).map((field: any) => {
          const fieldType = field.type || field.field_type;
          const recipientNum = field.recipient_number || 1;
          const customFieldId = field.custom_field_id || undefined;
          const label = fieldType === 'custom' && customFieldId && cfLookup[customFieldId]
            ? cfLookup[customFieldId]
            : field.label || fieldTypeLabels[fieldType] || fieldType?.charAt(0).toUpperCase() + fieldType?.slice(1) || 'Field';
          return {
            ...field,
            type: fieldType,
            label,
            pageNumber: field.pageNumber || field.page_number || 1,
            x: field.x ?? field.x_position,
            y: field.y ?? field.y_position,
            recipientId: recipientNum.toString(),
            iconColor: RECIPIENT_HEX_COLORS[recipientNum] || RECIPIENT_HEX_COLORS[1],
            customFieldId,
          };
        });

        // Restore recipients list from fields
        const usedRecipients = new Set<number>(formattedFields.map((f: any) => Number(f.recipientId) || 1));
        usedRecipients.add(1); // Always have at least recipient 1
        const restoredRecipients = Array.from(usedRecipients).sort((a: number, b: number) => a - b);
        setRecipients(restoredRecipients);
        setSelectedRecipient(restoredRecipients[0]);

        setTemplate({ ...tmplData, fields: formattedFields });
        setTitle(tmplData.template_title || '');
        setCategory(tmplData.document_category_id?.toString() || '');
        setNotes(tmplData.notes || '');

        if (tmplData.file_path) {
          fetchTemplateFile(id);
        }
      }
    } catch (error) {
      showToast({ title: 'Error', description: 'Failed to fetch template details', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  const fetchTemplateFile = async (templateId: string) => {
    try {
      const response = await api.get<Blob>(`/templates/${templateId}/download`, { responseType: 'blob' });
      if (response.data) {
        const url = URL.createObjectURL(response.data);
        setDocumentUrl(url);
      }
    } catch (error) {
      showToast({ title: 'Error', description: 'Failed to fetch template file', variant: 'destructive' });
    }
  };

  const MAX_FILE_SIZE_MB = 50;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileSelect = useCallback(async (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      showToast({
        title: 'File too large',
        description: `This file is ${fileSizeMB} MB. Maximum allowed is ${MAX_FILE_SIZE_MB} MB.`,
        variant: 'destructive',
      });
      return;
    }

    const filenameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
    setTitle(filenameWithoutExtension);
    setTemplate(prev => ({ ...prev, template_title: filenameWithoutExtension }));

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'doc' || ext === 'docx') {
      // DOC/DOCX: convert via backend, then preview the returned PDF
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('document', file);
        const response = await api.post<Blob>('/documents/convert', formData, {
          responseType: 'blob',
        });
        if (response.data) {
          const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
          const pdfFile = new File([pdfBlob], filenameWithoutExtension + '.pdf', { type: 'application/pdf' });
          setDocumentFile(pdfFile);
          const url = URL.createObjectURL(pdfBlob);
          setDocumentUrl(url);
        }
      } catch (error: any) {
        showToast({
          title: 'Conversion failed',
          description: error.response?.data?.message || 'Failed to convert document to PDF.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    } else {
      // PDF: use directly
      setDocumentFile(file);
      const url = URL.createObjectURL(file);
      setDocumentUrl(url);
    }
  }, [showToast]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFieldsChange = useCallback((fields: PlacedField[]) => {
    setTemplate(prev => ({ ...prev, fields }));
  }, []);

  // Recipient handlers
  const handleAddRecipient = useCallback(() => {
    setRecipients(prev => {
      if (prev.length >= 4) return prev;
      const next = [1, 2, 3, 4].find(n => !prev.includes(n));
      if (!next) return prev;
      const updated = [...prev, next].sort((a, b) => a - b);
      setSelectedRecipient(next);
      return updated;
    });
  }, []);

  const handleRemoveRecipient = useCallback((num: number) => {
    setRecipients(prev => {
      if (prev.length <= 1) return prev;
      const updated = prev.filter(n => n !== num);
      setSelectedRecipient(updated[0]);
      return updated;
    });
  }, []);

  // Count fields per recipient for the RecipientSelector guard
  const fieldsPerRecipient = useMemo(() => {
    const counts: Record<number, number> = {};
    template.fields.forEach(f => {
      const rn = Number(f.recipientId) || 1;
      counts[rn] = (counts[rn] || 0) + 1;
    });
    return counts;
  }, [template.fields]);

  const handleAddCustomField = useCallback(async (fieldName: string) => {
    if (!templateId) {
      // Template not yet saved — add locally so user can see it, but warn to save first
      setCustomFields(prev => [...prev, { id: Date.now(), field_name: fieldName }]);
      showToast({ title: 'Info', description: 'Save the template to persist this custom field', variant: 'default' });
      return;
    }
    const response = await api.post<any>(`/templates/${templateId}/custom-fields`, { field_name: fieldName }) as any;
    const cf = response?.custom_field || response?.data?.custom_field;
    if (cf) {
      setCustomFields(prev => [...prev, { id: cf.id, field_name: cf.field_name }]);
    }
  }, [templateId, showToast]);

  const handleSave = async () => {
    if (saving) return;
    if (!title.trim()) {
      showToast({ title: 'Validation', description: 'Please enter a template title', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('template_title', title);
      if (category && category !== 'none') {
        formData.append('document_category_id', category);
      }
      formData.append('notes', notes);

      formData.append('clear_fields', 'true');
      const validFields = template.fields.filter(field => field.type);
      validFields.forEach((field, index) => {
        formData.append(`fields[${index}][field_type]`, field.type);
        formData.append(`fields[${index}][x_position]`, (field.x ?? 0).toString());
        formData.append(`fields[${index}][y_position]`, (field.y ?? 0).toString());
        formData.append(`fields[${index}][page_number]`, (field.pageNumber ?? 1).toString());
        formData.append(`fields[${index}][width]`, (field.width || 100).toString());
        formData.append(`fields[${index}][height]`, (field.height || 40).toString());
        formData.append(`fields[${index}][recipient_number]`, (Number(field.recipientId) || 1).toString());
        if (field.customFieldId) {
          formData.append(`fields[${index}][custom_field_id]`, field.customFieldId.toString());
        }
      });

      if (documentFile) {
        formData.append('document', documentFile);
      }

      if (templateId) {
        formData.append('_method', 'PUT');
      }
      const url = templateId ? `/templates/${templateId}` : '/templates';
      const response = await api.post<ApiResponse<TemplateData>>(url, formData);

      const updatedTmpl = (response as any).template || (response as any).data?.template || response.data;

      if (updatedTmpl) {
        // Refresh custom fields from response
        const updatedCfs = (updatedTmpl.custom_fields || []).map((cf: any) => ({ id: cf.id, field_name: cf.field_name }));
        if (updatedCfs.length > 0) setCustomFields(updatedCfs);
        const cfLookup2: Record<number, string> = {};
        (customFields.length > 0 ? customFields : updatedCfs).forEach((cf: { id: number; field_name: string }) => { cfLookup2[cf.id] = cf.field_name; });

        const formattedFields = (updatedTmpl.fields || []).map((field: any) => {
          const fieldType = field.type || field.field_type;
          const recipientNum = field.recipient_number || 1;
          const customFieldId = field.custom_field_id || undefined;
          const label = fieldType === 'custom' && customFieldId && cfLookup2[customFieldId]
            ? cfLookup2[customFieldId]
            : field.label || fieldTypeLabels[fieldType] || fieldType?.charAt(0).toUpperCase() + fieldType?.slice(1) || 'Field';
          return {
            ...field,
            type: fieldType,
            label,
            pageNumber: field.pageNumber || field.page_number || 1,
            x: field.x ?? field.x_position,
            y: field.y ?? field.y_position,
            recipientId: recipientNum.toString(),
            iconColor: RECIPIENT_HEX_COLORS[recipientNum] || RECIPIENT_HEX_COLORS[1],
            customFieldId,
          };
        });

        setTemplate(prev => ({ ...prev, ...updatedTmpl, fields: formattedFields }));
        showToast({ title: 'Success', description: 'Template saved successfully', variant: 'success' });

        if (!templateId && updatedTmpl.id) {
          setTemplateId(updatedTmpl.id);
          window.history.replaceState(null, '', `/template-editor/${updatedTmpl.id}`);
        }
      }
    } catch (error: any) {
      showToast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save template',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendClick = () => {
    if (!templateId) {
      showToast({ title: 'Save first', description: 'Please save the template before sending', variant: 'destructive' });
      return;
    }
    setSendModalOpen(true);
  };

  const handleSendComplete = () => {
    setSendModalOpen(false);
    showToast({ title: 'Success', description: 'Template sent for signing', variant: 'success' });
    window.dispatchEvent(new Event('signing-count-changed'));
  };

  const handleBack = () => {
    navigate('/templates');
  };

  useEffect(() => {
    if (id) fetchTemplate();
    return () => {
      if (documentUrl) URL.revokeObjectURL(documentUrl);
    };
  }, [id, fetchTemplate]);

  // Upload prompt
  if (!documentUrl && !loading && !id) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 p-4">
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg w-full max-w-md">
          <Button variant="outline" className="mb-2" onClick={handleBack}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('templates:editor_back_to_templates')}
          </Button>
          <h2 className="text-xl font-semibold mb-4">{t('templates:editor_upload_title')}</h2>
          <p className="text-gray-500 mb-2 text-center">{t('templates:editor_upload_description')}</p>
          <p className="text-xs text-muted-foreground mb-6 text-center">
            {t('templates:editor_upload_max_size', { size: MAX_FILE_SIZE_MB })}
          </p>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button onClick={handleUploadClick} className="flex items-center">
            <Upload className="h-4 w-4 mr-2" />
            {t('templates:editor_select_file')}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p>{t('templates:editor_loading')}</p>
      </div>
    );
  }

  // Field types available for dragging
  const fieldTypes = Object.entries(fieldTypeLabels).map(([type, label]) => ({
    type,
    label,
  }));

  return (
    <div className="flex flex-col h-screen">
        {/* Template Header */}
        <header className="border-b border-border bg-card">
          <div className="p-4 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="template-category">{t('templates:editor_category_label')}</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="template-category">
                    <SelectValue placeholder={t('templates:editor_select_category')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('templates:editor_no_category')}</SelectItem>
                    {Array.isArray(categories) && categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="template-title">{t('templates:editor_title_label')}</Label>
                <Input
                  id="template-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('templates:editor_title_placeholder')}
                />
              </div>
              <div>
                <Label htmlFor="template-notes">{t('templates:editor_notes_label')}</Label>
                <Textarea
                  id="template-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('templates:editor_notes_placeholder')}
                  className="resize-none h-[38px]"
                />
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={handleBack}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink href="/templates">{t('templates:editor_breadcrumb_templates')}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink href="#">{title || t('templates:editor_upload_title')}</BreadcrumbLink>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
                  <SaveIcon className="h-4 w-4 mr-2" />
                  {t('common:actions.save')}
                </Button>
                <Button size="sm" onClick={handleSendClick} disabled={!templateId || template.status === 'inactive'}>
                  <SendIcon className="h-4 w-4 mr-2" />
                  {t('common:actions.send')}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Recipients + Field types to drag */}
          <div className="w-72 border-r border-border bg-card overflow-y-auto p-4">
            <RecipientSelector
              recipients={recipients}
              selectedRecipient={selectedRecipient}
              onSelectedChange={setSelectedRecipient}
              onAddRecipient={handleAddRecipient}
              onRemoveRecipient={handleRemoveRecipient}
              fieldsPerRecipient={fieldsPerRecipient}
            />

            <h3 className="text-sm font-semibold mb-2">{t('templates:editor_fields_title')}</h3>
            <p className="text-xs text-muted-foreground mb-4">{t('templates:editor_fields_description')}</p>
            <div className="space-y-2">
              {fieldTypes.map((ft) => {
                const rc = RECIPIENT_COLORS[selectedRecipient] || RECIPIENT_COLORS[1];
                return (
                  <DraggableField
                    key={ft.type}
                    id={`template-field-${ft.type}`}
                    type={ft.type as any}
                    label={ft.label}
                    recipientId={selectedRecipient.toString()}
                    iconColor={RECIPIENT_HEX_COLORS[selectedRecipient]}
                    className={`${rc.bg} ${rc.border}`}
                  />
                );
              })}

              {/* Custom fields */}
              {customFields.map((cf) => {
                const rc = RECIPIENT_COLORS[selectedRecipient] || RECIPIENT_COLORS[1];
                return (
                  <DraggableField
                    key={`custom-${cf.id}`}
                    id={`template-field-custom-${cf.id}`}
                    type="custom"
                    label={cf.field_name}
                    recipientId={selectedRecipient.toString()}
                    iconColor={RECIPIENT_HEX_COLORS[selectedRecipient]}
                    customFieldId={cf.id}
                    className={`${rc.bg} ${rc.border}`}
                  />
                );
              })}

              {/* Add Custom Field button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => setAddCustomFieldOpen(true)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('templates:editor_add_custom_field')}
              </Button>
            </div>
          </div>

          {/* Document Canvas */}
          <div className="flex-1 overflow-auto bg-gray-100">
            <DocumentCanvas
              documentUrl={documentUrl}
              fields={template.fields}
              onFieldsChange={handleFieldsChange}
              onDocumentUpload={handleFileSelect}
            />
          </div>
        </div>

        {/* Send Template Modal */}
        {templateId && (
          <SendTemplateModal
            open={sendModalOpen}
            onOpenChange={setSendModalOpen}
            templateId={templateId}
            templateTitle={title}
            recipientCount={recipients.length}
            onSendComplete={handleSendComplete}
          />
        )}

        {/* Add Custom Field Modal */}
        <AddCustomFieldModal
          open={addCustomFieldOpen}
          onOpenChange={setAddCustomFieldOpen}
          onAdd={handleAddCustomField}
        />
      </div>
  );
};

export default TemplateEditor;
